import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from '@core/components/Button';
import { useFeedQueryContext } from '@core/feed';
import { isFalsy } from '@core/helpers/boolean';
import { hasCTA } from '@core/helpers/video/hasCTA';
import { useFeatureFlags } from '@core/hooks/useFeatureFlags';
import { useResizeObserver } from '@core/hooks/useResizeObserver';
import { useContextTranslation } from '@core/i18n';
import { useLiveStreamActiveUser } from '@core/livestream/hooks/useLiveStreamActiveUser';
import { useEmbedFeedStore } from '@core/widgets/embedFeed/store';
import { useStore } from '@core/widgets/store';
import { videosSelector } from '@core/widgets/store/selectors';
import { useTrackEmbedVideoClick } from '@core/widgets/tracking/useTrackEmbedVideoClick';
import { CTAButton } from '@embed/components/Grid/CTAButton';
import { onNavigateToVideoHelper } from '@embed/components/VideoPlayer/helpers';
import { useFirstPartyLiveStream } from '@embed/hooks/useFirstPartyLiveStream';
import { StyledCaption, StyledCaptionContainer, StyledCTAContainer, StyledThumbnailContainerV2, StyledThumbnailContainerV2Body, } from './ThumbnailV2.styles';
/**
 * [CS-6259] Thumbnail container for medium carousel v2 cards
 */
export const ThumbnailContainerV2 = ({ children, video, appContext, params }) => {
    const { caption } = video;
    const { t } = useContextTranslation();
    const [width, setWidth] = useState(0);
    const { isFirstPartyLiveStream, openInFirstParty } = useFirstPartyLiveStream({
        appContext,
        params,
        video,
    });
    const videos = useStore(videosSelector);
    const { showVideoPlayer } = useEmbedFeedStore((state) => state.actions);
    const { setActiveWatch } = useLiveStreamActiveUser(video);
    const trackEmbedVideoClick = useTrackEmbedVideoClick();
    const [{ feed }] = useFeedQueryContext();
    const featureFlags = useFeatureFlags({
        channel: params.channel,
        channel_id: params.channel_id,
        target: params.target,
    });
    const hasCustomCTA = Boolean(featureFlags.player_cta_in_carousel) && hasCTA(video);
    const onClick = useCallback((event) => {
        event?.preventDefault();
        trackEmbedVideoClick(video);
        if (!isFirstPartyLiveStream) {
            openInFirstParty();
            return;
        }
        if (onNavigateToVideoHelper({
            params,
            appContext,
            video,
            feed,
            videos,
        })) {
            if (isFalsy(video.has_access_code)) {
                setActiveWatch();
            }
            showVideoPlayer({ video });
        }
    }, [
        appContext,
        feed,
        isFirstPartyLiveStream,
        openInFirstParty,
        params,
        setActiveWatch,
        showVideoPlayer,
        trackEmbedVideoClick,
        video,
        videos,
    ]);
    const containerRef = useResizeObserver(useCallback(([entry]) => {
        setWidth(entry.target.clientWidth);
    }, []));
    return (<StyledThumbnailContainerV2 thumbnailSize={params.size} ref={containerRef} css={css `
        --primary-button-background: var(--cta-button-background, #000);
        --button-color: var(--cta-button-background, #000);
        --button-font-weight: 400;
        --cta-button-font-weight: 400;
      `}>
      {children}
      <StyledThumbnailContainerV2Body thumbnailSize={params.size}>
        <StyledCaptionContainer>
          <StyledCaption>{caption}</StyledCaption>
        </StyledCaptionContainer>
        <StyledCTAContainer containerWidth={width}>
          {hasCustomCTA ? (<CTAButton video={video}/>) : (<Button aria-label={t('Watch Video "{{caption}}"', { caption })} onClick={onClick} css={css `
                &,
                & span {
                  color: var(--fw-cta-button-color);
                }
              `}>
              {t('Watch Video')}
            </Button>)}
        </StyledCTAContainer>
      </StyledThumbnailContainerV2Body>
    </StyledThumbnailContainerV2>);
};
